export const SERVER_TIMING_QUERY_PARAM = "_perf";

const now = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

export function diagnosticsEnabled(storageKey: string, queryParam: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.localStorage.getItem(storageKey) === "1" ||
      new URLSearchParams(window.location.search).get(queryParam) === "1"
    );
  } catch {
    return false;
  }
}

export async function measureDiagnosticStep<T>(
  prefix: string,
  label: string,
  enabled: boolean,
  action: () => Promise<T>,
  afterMeasure?: (startedAt: number) => void,
): Promise<T> {
  const startedAt = enabled ? now() : 0;
  try {
    return await action();
  } finally {
    if (enabled) {
      console.info(`[${prefix}] ${label}: ${Math.round(now() - startedAt)}ms`);
      afterMeasure?.(startedAt);
    }
  }
}

export function measureDiagnosticSync<T>(
  prefix: string,
  label: string,
  enabled: boolean,
  action: () => T,
): T {
  const startedAt = enabled ? now() : 0;
  try {
    return action();
  } finally {
    if (enabled) {
      console.info(`[${prefix}] ${label}: ${Math.round(now() - startedAt)}ms`);
    }
  }
}

export function withServerTiming(url: string, enabled: boolean): string {
  if (!enabled) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${SERVER_TIMING_QUERY_PARAM}=1`;
}

export function logResourceTiming(
  prefix: string,
  label: string,
  urlFragment: string,
  enabled: boolean,
) {
  if (!enabled || typeof performance === "undefined") return;
  const entries = performance
    .getEntriesByType("resource")
    .filter(
      (entry): entry is PerformanceResourceTiming =>
        entry instanceof PerformanceResourceTiming &&
        entry.initiatorType === "fetch" &&
        entry.name.includes(urlFragment),
  );
  const entry = entries[entries.length - 1];
  if (!entry) return;
  logResourceTimingEntry(prefix, label, entry);
}

export function logResourceTimingsSince(
  prefix: string,
  label: string,
  urlFragment: string,
  enabled: boolean,
  startedAt: number,
) {
  if (!enabled || typeof performance === "undefined") return;
  const entries = performance
    .getEntriesByType("resource")
    .filter(
      (entry): entry is PerformanceResourceTiming =>
        entry instanceof PerformanceResourceTiming &&
        entry.initiatorType === "fetch" &&
        entry.name.includes(urlFragment) &&
        entry.startTime >= startedAt,
    );
  entries.forEach((entry, index) => {
    const url = new URL(entry.name, window.location.origin);
    logResourceTimingEntry(
      prefix,
      `${label} request ${index + 1} ${url.pathname}`,
      entry,
    );
  });
}

function logResourceTimingEntry(
  prefix: string,
  label: string,
  entry: PerformanceResourceTiming,
) {
  const total = Math.round(entry.duration);
  if (entry.responseStart <= 0 || entry.requestStart <= 0) {
    console.info(
      `[${prefix}] ${label} network: total=${total}ms (detailed cross-origin timing unavailable)`,
    );
    return;
  }
  const queued = Math.max(0, Math.round(entry.requestStart - entry.fetchStart));
  const ttfb = Math.max(0, Math.round(entry.responseStart - entry.requestStart));
  const download = Math.max(0, Math.round(entry.responseEnd - entry.responseStart));
  const server = entry.serverTiming
    .map((metric) => {
      const duration = Number.isFinite(metric.duration)
        ? `${Math.round(metric.duration)}ms`
        : "";
      const description = metric.description ? ` (${metric.description})` : "";
      return `${metric.name}=${duration}${description}`;
    })
    .join(" ");
  console.info(
    `[${prefix}] ${label} network: queue=${queued}ms ttfb=${ttfb}ms download=${download}ms total=${total}ms${server ? ` server: ${server}` : ""}`,
  );
}
